import { countryToAlpha2 } from "country-to-iso";

import {
  AvatarFields,
  CountryCode,
  CurrencyCode,
  DateFormat,
  isCountryCode,
  isCurrencyCode,
  TimeFormat,
} from "@/types/common.types";

/**
 * Shape shared by every REST DTO that carries a user avatar: the storage file
 * (`{ fileId, url }`) plus the legacy free-text URL. Mirrors `DashboardUserRef`,
 * `DashboardUserListItem`, `DashboardCurrentUser`, etc.
 */
type ApiAvatarSource = {
  avatarFile?: { url?: string | null; fileId?: string | null } | null;
  legacyAvatarUrl?: string | null;
};

/**
 * Pass the raw avatar fields through from a REST DTO to the domain
 * `AvatarFields` shape. Consumers resolve the final URL at render via
 * `resolveAvatarUrl` — mappers do NOT pre-resolve to a single string.
 */
export const mapAvatarFields = (source: ApiAvatarSource | null | undefined): AvatarFields => ({
  avatarFile: source?.avatarFile
    ? { url: source.avatarFile.url ?? null, fileId: source.avatarFile.fileId ?? null }
    : null,
  legacyFileUrl: source?.legacyAvatarUrl ?? null,
});

export const mapToCountryCode = (country: string): CountryCode => {
  const countryCode = mapToCountryCodeNullable(country);

  if (!countryCode) {
    throw new Error(`Expected country: ${country}`);
  }

  return countryCode;
};

const UK_CONSTITUENT_COUNTRY_CODES: Record<string, CountryCode> = {
  England: "GB-ENG",
  Scotland: "GB-SCT",
  Wales: "GB-WLS",
  "Northern Ireland": "GB-NIR",
};

/**
 * Legacy display names → current ISO codes, for entries the legacy admin's
 * country list spells differently to what `countryToAlpha2` matches. Without
 * these they resolve to `null` and are silently dropped, which for a country
 * eligibility gate SILENTLY WIDENS it. Mirrors the BE `LEGACY_COUNTRY_NAME_CODES`.
 *
 * "Netherlands Antilles" (`AN`) and "Serbia and Montenegro" (`CS`) are absent on
 * purpose: both were withdrawn from ISO, are not in `CountryCode`, and are not
 * selectable in the current UI.
 */
const LEGACY_COUNTRY_NAME_CODES: Record<string, CountryCode> = {
  "congo, the democratic republic of the": "CD",
  "iran, islamic republic of": "IR",
  "korea, democratic people's republic of": "KP",
  "libyan arab jamahiriya": "LY",
  "macedonia, the former yugoslav republic of": "MK",
  "palestinian territory, occupied": "PS",
  swaziland: "SZ",
  "tanzania, united republic of": "TZ",
  "viet nam": "VN",
};

export const mapToCountryCodeNullable = (country: string): CountryCode | null => {
  if (isCountryCode(country)) return country;
  if (UK_CONSTITUENT_COUNTRY_CODES[country]) return UK_CONSTITUENT_COUNTRY_CODES[country];
  const legacy = LEGACY_COUNTRY_NAME_CODES[country.toLowerCase()];
  if (legacy) return legacy;
  const countryCode = countryToAlpha2(country);
  if (!countryCode) return null;
  return isCountryCode(countryCode) ? countryCode : null;
};

/**
 * Array variant of {@link mapToCountryCodeNullable}: normalizes each entry,
 * drops the un-mappable ones and de-dupes, preserving order.
 *
 * Mirrors the BE `mapToCountryCodes` so both sides converge on the same set.
 */
export const mapToCountryCodeList = (countries: readonly string[]): CountryCode[] => {
  const seen = new Set<CountryCode>();

  return countries.reduce<CountryCode[]>((acc, country) => {
    const code = mapToCountryCodeNullable(country);
    if (code && !seen.has(code)) {
      seen.add(code);
      acc.push(code);
    }
    return acc;
  }, []);
};

export const mapToCurrencyCode = (currencyProp: string): CurrencyCode | null => {
  const currency = currencyProp.toUpperCase();
  return isCurrencyCode(currency) ? currency : null;
};

const supportedTimeZones = Intl.supportedValuesOf("timeZone");

export const mapToTimeZone = (rawTimeZone: string): string | null => {
  if (supportedTimeZones.includes(rawTimeZone)) return rawTimeZone;
  return (
    supportedTimeZones.find((tz) => rawTimeZone.toLowerCase().includes(tz.toLowerCase())) ?? null
  );
};

export const mapToDateFormat = (dateFormat: string): DateFormat => {
  switch (dateFormat) {
    case "EUROPEAN":
      return "european";
    case "AMERICAN":
      return "american";
    case "ISO":
      return "iso";
    default:
      throw new Error(`Expected date format: ${dateFormat}`);
  }
};

export const mapToTimeFormat = (timeFormat: string): TimeFormat => {
  switch (timeFormat) {
    case "H12":
      return "h12";
    case "H24":
      return "h24";
    default:
      throw new Error(`Expected time format: ${timeFormat}`);
  }
};
